import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from '@/hooks/useTranslation';
import { useToolSummaries } from '@/features/traceability/hooks/useToolSummaries';
import { pickersApplyTo, toolWriteErrorMessage } from '@/features/traceability/utils/toolForm';
import { ExistingToolIds } from '@/features/traceability/components/pickers/ExistingToolIds';
import { takenToolId } from '@/features/traceability/components/pickers/pickerHelpers';
import { ReferencePicker } from '@/features/traceability/components/pickers/ReferencePicker';
import { keepOpenWhileListOpen } from '@/features/traceability/components/pickers/keepOpenWhileListOpen';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { StatusCard } from '@/components/ui/card';

type AddToolDialogProps = {
  open: boolean;
  toolId: string;
  onClose: () => void;
  onSuccess: (toolId: string) => void;
};

export function AddToolDialog({ open, toolId, onClose, onSuccess }: AddToolDialogProps) {
  const http = useApiClient();
  const { t } = useTranslation();
  const { summaries: companySummaries } = useToolSummaries(open);

  const [reference, setReference] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const submitControllerRef = useRef<AbortController | null>(null);
  const summaries = pickersApplyTo(companyName) ? companySummaries : null;
  const taken = takenToolId(toolId, summaries);

  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  // Reset form when dialog opens or toolId changes
  useEffect(() => {
    if (open) {
      setReference('');
      setCompanyName('');
      setError(null);
    }
  }, [open, toolId]);

  // Abort in-flight request on unmount
  useEffect(() => {
    return () => {
      submitControllerRef.current?.abort();
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (!traceabilityApiClient || taken) {
      return;
    }

    submitControllerRef.current?.abort();
    const ac = new AbortController();
    submitControllerRef.current = ac;

    // Guard: both fields are required
    if (!reference.trim() || !companyName.trim()) {
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const result = await traceabilityApiClient.toolwrite(
        {
          tool: {
            toolId,
            reference: reference.trim(),
            companyName,
            traces: [],
          },
        },
        { signal: ac.signal }
      );

      if (ac.signal.aborted) return;

      if (result.ok) {
        onSuccess(toolId);
      } else {
        setError(toolWriteErrorMessage(result, t, 'toolView.addToolDialog.errors.default'));
      }
    } catch {
      if (ac.signal.aborted) return;
      setError(t('toolView.addToolDialog.errors.default'));
    } finally {
      if (!ac.signal.aborted) setIsLoading(false);
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      onClose();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent onEscapeKeyDown={keepOpenWhileListOpen}>
        <DialogHeader>
          <DialogTitle>{t('toolView.addToolDialog.title')}</DialogTitle>
          <DialogDescription>{t('toolView.addToolDialog.description')}</DialogDescription>
        </DialogHeader>

        {/* Scanned ID — read-only badge */}
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-foreground">
            {t('toolView.addToolDialog.scannedId')}
          </span>
          <div className="bg-muted rounded-lg px-3 py-2 font-mono text-sm font-semibold text-foreground">
            {toolId}
          </div>
          <ExistingToolIds value={toolId} summaries={summaries} showSimilar={false} />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="space-y-2">
            <Label htmlFor="add-tool-reference">
              {t('toolView.addToolDialog.fields.reference.label')}
            </Label>
            <ReferencePicker
              id="add-tool-reference"
              value={reference}
              onChange={setReference}
              summaries={summaries}
              placeholder={t('toolView.addToolDialog.fields.reference.placeholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-tool-company">
              {t('toolView.addToolDialog.fields.company.label')}
            </Label>
            <Input
              id="add-tool-company"
              type="text"
              placeholder={t('toolView.addToolDialog.fields.company.placeholder')}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('toolView.addToolDialog.actions.cancel')}
            </Button>
            <Button type="submit" disabled={taken !== null} loading={isLoading}>
              {t('toolView.addToolDialog.actions.add')}
            </Button>
          </div>

          {error && <StatusCard type="error" message={error} />}
        </form>
      </DialogContent>
    </Dialog>
  );
}
